/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, OnInit, OnDestroy, HostListener, ViewChild} from '@angular/core';

import {Subscription} from "rxjs";

import {AuthenticationService} from '../services/authentication.service';
import {Url_Base} from '../rest-constants';
import {DialogService} from "../services/dialog.service";
import {UserService} from '../services/user.service';
import {UserDialogService} from '../services/user-dialog.service';
import {User} from '../models/user';
import {PagePermissionType} from "../models/page-permission-type";
import {PagePermissionService} from "../services/page-permission.service";
import {Status} from "../models/dialog-instance";
import {Router} from "@angular/router";
import {LicensingService} from "../services/licensing.service";
import {DialogNotificationService} from "../services/dialog-notification.service";
import {PingService} from "../services/ping.service";
import { ImageService } from 'app/services/image.service';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';


@Component({
  selector: 'app-header',
  templateUrl: './header.component.html'
})
export class HeaderComponent implements OnInit, OnDestroy {

      /** Reference to the SimpleSuccessErrorDialog used to display error */
    @ViewChild('successErrorDialog') 
    simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Page Permission Types - Used by UI to determine whether to show links to pages or not - Defined here so that the UI can reference the enums
  mPageTypes = PagePermissionType;

  //Holds the current number of Active Alerts - Displayed by the UI in the top right, on the link to the Security Page
  mAlertCount = 0;

  //Subscription to any new Alerts that are coming in, used to refresh the count displayed by the UI
  mDialogWsSubscription: Subscription = null;
  mPingServiceSubscription: Subscription = null;
  lastPingTime = new Date().getDate();
  licensingExpirationDate = null;

  headerDate = new Date();

  serverStatus = true;
  oldServerStatus = true;

  /** Holds the Links / Line Items that should be displayed under the "System" Drop Down */
  systemLinks: SystemLineItem[] = [];

  constructor(
    private mRouter: Router,
    private mAuthService: AuthenticationService,
    private mUserDialogService: UserDialogService,
    private mUserService: UserService,
    private mDialogService: DialogService,
    private mDialogNotificationService: DialogNotificationService,
    private mPagePermissionService: PagePermissionService,
    private mLicensingService: LicensingService,
    private mPingService: PingService,
    private mImageService: ImageService
  ) { }

  ngOnInit(): void {
    //Refresh the Alert Count current displayed
    this.refreshAlertCount();

    this.mLicensingService.getCompiledLicense().then(
      response => {
        if (response.expiring_licenses && response.expiring_licenses.length > 0) {
          this.licensingExpirationDate = response.earliest_expiration_date;
        }
      }
    );

    this.addSubscriptions();

    this.mPingServiceSubscription = this.mPingService.getPingServerEventListeners().subscribe( pingResult => {
      this.serverStatus = pingResult;

      const currentPingTime = new Date().getDate();
      // If the time between pings is greater than 30 minutes....
      if ((currentPingTime - this.lastPingTime) > 1000 * 60 * 30) {
        console.warn("Reinitializing Authorization Service due to time between pings > 30 minutes");
        this.mAuthService.initialize();
      }

      // if the status went from true is false....
      if (this.oldServerStatus == true && this.serverStatus == false) {
      }

      // If the status went from false to true... re-open the web sockets
      if (this.oldServerStatus == false && this.serverStatus == true) {
        console.warn("Reinitializing Authorization Service due to server down then back up");
        this.mAuthService.initialize();
      }

      this.oldServerStatus = this.serverStatus;
      this.lastPingTime = currentPingTime;

    });

    this.mPingService.pingServerBackgroundThread();

    this.refreshSystemLinkPermissions();
  }

  ngOnDestroy(): void {
    this.removeSubscriptions();

    //Destroy the subscription to the Pings if it exists, necessary cleanup
    if (this.mPingServiceSubscription != null) {
      this.mPingService.stopPingingServer();
      this.mPingServiceSubscription.unsubscribe();
      this.mPingServiceSubscription = null;
    }

  }

  addSubscriptions(): void {
    //Subscribe to any new Alerts so that the count can be refreshed
    this.mDialogWsSubscription = this.mDialogNotificationService
      .getRefreshDialogsListener()
      .subscribe(
        () => {
          console.log("header component - dialogNotificationService called");
          this.refreshAlertCount();
        }
      );
  }

  removeSubscriptions(): void {
    //Destroy the subscription to the Alerts if it exists, necessary cleanup
    if (this.mDialogWsSubscription != null) {
      this.mDialogWsSubscription.unsubscribe();
      this.mDialogWsSubscription = null;
    }

  }

  getAuthServiceUser(): User {
    return this.mAuthService.getUser();
  }


  getPathForUserImage(): string {
    return this.mImageService.getImageSourceForUser(this.mAuthService.getUser());
  }

  logoutClicked(): void {
    //Route to the Login Page, this also logs the User out (Whenever the page is displayed, any logged in User is logged out)
    this.mRouter.navigateByUrl('/login');
  }

  async editProfileClicked(): Promise<void> {
    //Get the information on the currently logged in User
    const user = this.mAuthService.getUser();

    try {
      //Get any updates to the User since it logged in
      const refreshedUser = await this.mUserService.getUserForId(user.id);

      //Get the Tags currently assigned to the User
      const tags = await this.mUserService.getTagsForUser(refreshedUser);

      console.log("Tags assigned to user: " + tags);

      //Send a message to the User Dialog to open in "Manage Profile" mode for the User/Tag information
      this.mUserDialogService.updateUserProfile(refreshedUser);
    } catch (error) {
      this.handleError(error);
    }
  }

  async refreshAlertCount(): Promise<void> {
    try {
      this.mAlertCount = await this.mDialogService.getDialogInstanceStatusCount(Status.OPEN);
      console.log("refreshAlertCount(): getDialogInstanceStatusCount = " + this.mAlertCount);
    } catch (error) {
      this.handleError(error);
    }
  }

  canReadPage(pageType: PagePermissionType): boolean {
    if (pageType === null) {
      return false;
    } else if (pageType == PagePermissionType.LicensingPage) {
      return this.mPagePermissionService.canRead(pageType);
    } else {
      const validLicense = this.isLicenseValid();
      if (validLicense === true) {
        return this.mPagePermissionService.canRead(pageType);
      } else {
        return false;
      }
    }
  }

  isLicenseValid(): boolean {
    return true;
    //const currentLicense = this.mAuthService.getCurrentLicenseInfo();
    //return currentLicense === null? false : (currentLicense.is_valid === null? false : currentLicense.is_valid);
  }

  handleError(error: any): void {
    console.error("HeaderComponent.handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      // Show the error in the SimpleSuccessErrorDialogComponent
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

  /** 
   * Refreshes the "systemLinks" array representing the page links that the 
   * User has access to that should exist under the "System" Drop Down.
   */
  private refreshSystemLinkPermissions(): void {
    const links: SystemLineItem[] = [];
    let needsDivider = false;

    if (this.canReadPage(PagePermissionType.LocatePage)) {
      links.push(LI_Locate);
      needsDivider = true;
    }
    if (needsDivider) {
      links.push(LI_Divider);
      needsDivider = false;
    }

    if (this.canReadPage(PagePermissionType.UsersPage)) {
      links.push(LI_Users);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.RoleManagerPage)) {
      links.push(LI_RoleManager);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.SetMmcAffiliatesPage)) {
      links.push(LI_SetMmcAffiliates);
      needsDivider = true;
    }
    if (needsDivider) {
      links.push(LI_Divider);
      needsDivider = false;
    }

    if (this.canReadPage(PagePermissionType.InfrastructurePage)) {
      links.push(LI_Infrastructure);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.AssetsPage)) {
      links.push(LI_Assets);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.PeoplePage)) {
      links.push(LI_People);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.SitesPage)) {
      links.push(LI_Sites);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.GtinValidationSetsPage)) {
      links.push(LI_GtinValidationSets);
      needsDivider = true;
    }
    if (needsDivider) {
      links.push(LI_Divider);
      needsDivider = false;
    }

    if (this.canReadPage(PagePermissionType.TriggersPage)) {
      links.push(LI_Triggers);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.ReactionsPage)) {
      links.push(LI_Reactions);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.RulesPage)) {
      links.push(LI_Rules);
      needsDivider = true;
    }
    if(this.canReadPage(PagePermissionType.RulesPage)) {
      links.push(LI_Dialogs);
      needsDivider = true;
    }
    if (this.canReadPage(PagePermissionType.WorkflowsPage)) {
      links.push(LI_Workflows);
      needsDivider = true;
    }
    if (needsDivider) {
      links.push(LI_Divider);
      needsDivider = false;
    }

    if (this.canReadPage(PagePermissionType.SettingsPage)) {
      links.push(LI_Settings);
    }
    if (this.canReadPage(PagePermissionType.LicensingPage)) {
      links.push(LI_Licensing);
    }
    if (this.canReadPage(PagePermissionType.HistoryPage)) {
      links.push(LI_History);
    }
    if (this.canReadPage(PagePermissionType.SwaggerApiPage)) {
      links.push(LI_Swagger);
    }

    if (links[links.length - 1] === LI_Divider) {
      links.splice(links.length - 1, 1);
    }
    this.systemLinks = links;
  }

}

/** Represents a Line Item / Link to a page under the "System" Drop Down */
interface SystemLineItem {
  isDivider: boolean;
  name: string;
  path: string[];
  iconClasses: string;
  externalHref?: string;
}

/** Represents the "Locate" page link in the "System" Drop Down menu */
const LI_Locate: SystemLineItem = {
  isDivider: false,
  name: "LOCATE",
  path: ["/locate"],
  iconClasses: "fa fa-search"
};

/** Represents the "Users" page link in the "System" Drop Down menu */
const LI_Users: SystemLineItem = {
  isDivider: false,
  name: "USERS",
  path: ["/users"],
  iconClasses: "fa fa-users"
};

/** Represents the "Role Manager" page link in the "System" Drop Down menu */
const LI_RoleManager: SystemLineItem = {
  isDivider: false,
  name: "ROLE MANAGER",
  path: ["/roleManager"],
  iconClasses: "fa fa-plus-square"
};

/** Represents the "Set MMC Affiliates" page link in the "System" Drop Down menu */
const LI_SetMmcAffiliates: SystemLineItem = {
  isDivider: false,
  name: "SET MMC AFFILIATES",
  path: ["/users/mmc-info"],
  iconClasses: "fa fa-print"
};

/** Represents the "Infrastructure" page link in the "System" Drop Down menu */
const LI_Infrastructure: SystemLineItem = {
  isDivider: false,
  name: "INFRASTRUCTURE",
  path: ["/infrastructure"],
  iconClasses: "fa fa-cogs"
};

/** Represents the "Assets" page link in the "System" Drop Down menu */
const LI_Assets: SystemLineItem = {
  isDivider: false,
  name: "ASSETS",
  path: ["/efassets"],
  iconClasses: "fa fa-star-o"
};

/** Represents the "People" page link in the "System" Drop Down menu */
const LI_People: SystemLineItem = {
  isDivider: false,
  name: "PEOPLE",
  path: ["/people"],
  iconClasses: "fa fa-users"
};

/** Represents the "Sites" page link in the "System" Drop Down menu */
const LI_Sites: SystemLineItem = {
  isDivider: false,
  name: "SITES",
  path: ["/sites"],
  iconClasses: "fa fa-map-o"
};

/** Represents the "Sites" page link in the "System" Drop Down menu */
const LI_GtinValidationSets: SystemLineItem = {
  isDivider: false,
  name: "GTIN SETS",
  path: ["/gtin-validation-sets"],
  iconClasses: "fa fa-map-o"
};

/** Represents the "Triggers" page link in the "System" Drop Down menu */
const LI_Triggers: SystemLineItem = {
  isDivider: false,
  name: "TRIGGERS",
  path: ["/triggers"],
  iconClasses: "fa fa-bolt"
};

/** Represents the "Reactions" page link in the "System" Drop Down menu */
const LI_Reactions: SystemLineItem = {
  isDivider: false,
  name: "REACTIONS",
  path: ["/reactions"],
  iconClasses: "fa fa-flask"
};

/** Represents the "Rules" page link in the "System" Drop Down menu */
const LI_Rules: SystemLineItem = {
  isDivider: false,
  name: "RULES",
  path: ["/rules"],
  iconClasses: "fa fa-check-square-o"
};

/** Represents the "Dialogs" page link in the "System" Drop Down menu */
const LI_Dialogs: SystemLineItem = {
  isDivider: false,
  name: "DIALOGS",
  path: ["/dialogs"],
  iconClasses: "fa fa-list-alt"
};

/** Represents the "Workflows" page link in the "System" Drop Down menu */
const LI_Workflows: SystemLineItem = {
  isDivider: false,
  name: "WORKFLOWS",
  path: ["/workflows"],
  iconClasses: "fa fa-check-square-o"
};

/** Represents the "Settings" page link in the "System" Drop Down menu */
const LI_Settings: SystemLineItem = {
  isDivider: false,
  name: "SETTINGS",
  path: ["/settings"],
  iconClasses: "fa fa-wrench"
};

/** Represents the "Licensing" page link in the "System" Drop Down menu */
const LI_Licensing: SystemLineItem = {
  isDivider: false,
  name: "LICENSING",
  path: ["/licensing"],
  iconClasses: "fa fa-server"
};

/** Represents the "History" page link in the "System" Drop Down menu */
const LI_History: SystemLineItem = {
  isDivider: false,
  name: "HISTORY",
  path: ["/history"],
  iconClasses: "fa fa-history"
};

/** Represents the "Swagger" page link in the "System" Drop Down menu */
const LI_Swagger: SystemLineItem = {
  isDivider: false,
  name: "SWAGGER API",
  path: [],
  externalHref: Url_Base.replace(/\/api$/, '') + '/swagger-ui/index.html',
  iconClasses: "fa fa-external-link"
};

/** Represents a "Divider" in the "System" Drop Down menu */
const LI_Divider: SystemLineItem = {
  isDivider: true,
  name: null,
  path: null,
  iconClasses: null
};
